import { EditorState, Plugin, PluginKey, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import { urlsafeB64Decode, urlsafeB64Encode } from "~/lib/base64";
import type { EditorContextParticipant } from "~/stream/NoteCard";

// List of all node types that should be updated when the context value
// changes. Nodes of these types will be decorated with the current context
// value, and as such will update the context changes.
const dependentNodeTypes: readonly string[] = ["agenda", "goal", "todo", "header-one", "tag"];

export const ProseReactContextPluginKey = new PluginKey("prose-react-context");
const ProseReactContextPlugin = (config: { initialContext: { [key: string]: unknown } }) => {
	let prevContext: { [key: string]: unknown } | undefined;

	return new Plugin({
		key: ProseReactContextPluginKey,
		props: {
			// Decorations are only used to make ProseMirror update the nodes that
			// depend on the value of the context (instead of forcibly recreating
			// every node in the document every context change).
			decorations(editorState) {
				const decos: Decoration[] = [];
				const { doc } = editorState;
				const ctx = getContext(editorState);

				// 2 cases:
				// - context changed; we need to put a decoration for every node
				// - blocks in document changed; we don't need to do anything
				if (prevContext && ctx === prevContext) return;

				doc.descendants((child, pos) => {
					if (dependentNodeTypes.includes(child.type.name)) {
						decos.push(Decoration.node(pos, pos + child.nodeSize, {}, ctx));
					}
				});

				return DecorationSet.create(doc, decos);
			},
		},
		state: {
			init: (_config, state) => {
				const prevState = ProseReactContextPluginKey.getState(state);
				if (prevState) {
					return prevState;
				}
				return { ...config.initialContext };
			},
			apply: (tr, value, _oldState, _newState) => {
				const updates = tr.getMeta("updateContext");
				prevContext = value;
				if (updates) {
					return { ...value, ...updates };
				}
				return value;
			},
		},
	});
};

export const getContext = (state: EditorState) => {
	return ProseReactContextPluginKey.getState(state);
};

// a command to update context
export const updateContext = (updates: object) => (state: EditorState, dispatch?: (tr: Transaction) => void) => {
	const tr = state.tr;
	updateContextTr(updates, tr);
	if (dispatch) {
		dispatch(tr);
	}
	return true;
};

export const updateContextTr = (updates: object, tr: Transaction) => {
	return tr.setMeta("updateContext", updates);
};

export function encodeUserId(intId: number): string {
	return urlsafeB64Encode(`User:${intId}`);
}

export function getCurrentUser(state: EditorState): EditorContextParticipant | undefined {
	const currentUser = window.INITIAL_STATE.user;
	if (!currentUser) return undefined;
	let currentUserId: string | undefined;
	if (typeof currentUser.id === "string") {
		try {
			// Try to base64 decode it, if it succeeds it's probably a Relay ID, and
			// if it fails we'll assume it's an integer ID.
			urlsafeB64Decode(currentUser.id);
			currentUserId = currentUser.id;
		} catch {
			const n = Number.parseInt(currentUser.id, 10);
			if (Number.isNaN(n)) {
				throw new Error(`Invalid user ID: ${currentUser.id}`);
			}
			currentUserId = encodeUserId(n);
		}
	} else {
		// It's a number, encode a Relay ID with it.
		currentUserId = encodeUserId(currentUser.id);
	}
	const userObj = getContext(state)?.participants?.find((u: EditorContextParticipant) => u.id === currentUserId);
	return userObj;
}

export default ProseReactContextPlugin;
